import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { TipService } from '@interacta-shared/feature-tip';
import {
    ButtonComponent,
    DeltaViewComponent,
    IconButtonComponent,
    MenuDecoratorDirective,
    MenuDialogComponent,
    ToggleIconButtonComponent,
} from '@interacta-shared/ui';
import { AIFeedbackComponent, FeedbackChip } from '@interacta-shared/ui-ai';
import { KeyCode } from '@interacta-shared/util';
import {
    AI_REFERENCE_CLASS,
    AI_REFERENCE_ID_DATASET_KEY,
    withAIReferenceChips,
} from '@modules/ai-shared/helpers/ai-shared-delta-reference.util';
import {
    formatMessage,
    isModelMessageError,
} from '@modules/ai-shared/helpers/ai-shared.utils';
import { registerAIReferenceQuill } from '@modules/ai-shared/helpers/quill-ai-reference-blot';
import {
    AISharedGenericReference,
    AISharedMessageType,
    AISharedModelMessage,
    AISharedSmartAction,
} from '@modules/ai-shared/models/ai-shared.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    concat,
    concatMap,
    from,
    ignoreElements,
    of,
    Subscription,
    timer,
} from 'rxjs';
import { AISharedReferencesComponent } from '../ai-shared-references/ai-shared-references.component';

/** Per-unit fade duration (keep in sync with the ai-delta-reveal CSS animation). */
const REVEAL_UNIT_MS = 250;
/** Upper bound on the whole staggered reveal; the per-unit step shrinks to fit. */
const REVEAL_MAX_TOTAL_MS = 4000;

@Component({
    selector: 'interacta-ai-shared-model-message-balloon',
    templateUrl: './ai-shared-model-message-balloon.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AIFeedbackComponent,
        AISharedReferencesComponent,
        ButtonComponent,
        DeltaViewComponent,
        IconButtonComponent,
        MatTooltipModule,
        MenuDecoratorDirective,
        MenuDialogComponent,
        ToggleIconButtonComponent,
        TranslatePipe,
    ],
    host: {
        '(click)': 'onReferenceClick($event)',
        '(keydown)': 'onReferenceKeydown($event)',
    },
    styles: `
        interacta-ai-shared-model-message-balloon {
            display: block;
        }

        /*
         * Gemini-style reveal for a freshly rendered Delta answer: each fade unit
         * (see collectRevealUnits — paragraphs and list items) fades in and rises
         * a couple px, staggered by its index. Everything is laid out from the
         * start (opacity + transform are composited), so there is no layout shift.
         * ViewEncapsulation.None (like delta-view itself) lets these ai-delta-*
         * namespaced selectors reach the rendered Quill content without ::ng-deep.
         */
        .ai-delta-reveal .ai-reveal-unit {
            animation: ai-delta-reveal 250ms ease-out both;
            /* Per-unit delay (ms) is computed in armReveal: a dynamic step keeps
               every unit staggered while bounding the total time. */
            animation-delay: calc(var(--ai-delta-delay, 0) * 1ms);
        }

        /* Hides the whole answer synchronously (before first paint) until the
           per-unit reveal is armed, covering any frame where Quill lays out its
           content asynchronously — otherwise the full answer flashes. */
        .ai-delta-hidden {
            opacity: 0;
        }

        /* Footer fades in (same motion) when it mounts after the reveal. */
        .ai-delta-footer {
            animation: ai-delta-reveal 500ms ease-out both;
        }

        @keyframes ai-delta-reveal {
            from {
                opacity: 0;
                transform: translateY(2px);
            }
            to {
                opacity: 1;
                transform: none;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .ai-delta-footer,
            .ai-delta-reveal .ai-reveal-unit {
                animation: none;
            }
        }
    `,
    encapsulation: ViewEncapsulation.None,
})
export class AISharedModelMessageBalloonComponent implements AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly tipService = inject(TipService);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly translateService = inject(TranslateService);
    private readonly gaService = inject(GoogleAnalyticsService);

    message = input.required<AISharedModelMessage>();
    isLast = input.required<boolean>();
    enableRevealAnimation = input(false);
    readonly senderName = input<string | null>(null);
    /** Smart actions send at once, so they are off while an answer is pending. */
    readonly smartActionsDisabled = input(false);

    scrollToBottom = output<void>();
    messageAnimated = output<void>();
    readonly revealed = output<void>();
    retry = output<void>();
    sendFeedbackLike = output<boolean | undefined>();
    sendFeedbackDetail = output<{
        chips: FeedbackChip[];
        moreDetails: string | null;
    }>();
    readonly askReference = output<AISharedGenericReference>();
    readonly smartAction = output<{
        reference: AISharedGenericReference;
        action: AISharedSmartAction;
    }>();

    readonly displayedMessage = signal('');
    readonly animateDelta = signal(false);
    readonly showReferences = signal(false);
    // Footer (feedback, copy, sources, disclaimer) is hidden while the answer is
    // still revealing; shown at once when the reveal finishes (or immediately for
    // non-animated messages).
    readonly contentRevealed = signal(true);

    readonly isError = computed(() => isModelMessageError(this.message()));
    readonly hideRetryButton = computed(
        () =>
            this.message().messageType ===
            AISharedMessageType.AI_CREDIT_LIMIT_EXCEEDED,
    );
    readonly canSendFeedback = computed(
        () => this.isLast() || this.message().feedbackLike === undefined,
    );

    readonly isModelMessageError = isModelMessageError;

    /** Delta actually rendered: the BE reference markers become chip embeds. */
    readonly displayDelta = computed(() => {
        const delta = this.message().messageDelta;
        const references = this.message().deltaReferences ?? [];
        if (!delta || !references.length) return delta ?? null;

        return withAIReferenceChips(
            delta,
            references,
            (key, params) =>
                this.translateService.instant(key, params) as string,
        );
    });

    /** Chip whose sources are listed in the popover. */
    private readonly openedReference = signal<{
        origin: CdkOverlayOrigin;
        id: number;
    } | null>(null);
    readonly popoverOrigin = computed(
        () => this.openedReference()?.origin ?? null,
    );
    readonly popoverSources = computed(() => {
        const id = this.openedReference()?.id;
        return (
            this.message().deltaReferences?.find(
                (reference) => reference.id === id,
            )?.sources ?? []
        );
    });

    private animationSubscription: Subscription | undefined;

    constructor() {
        registerAIReferenceQuill();
    }

    ngAfterViewInit(): void {
        if (this.message().messageDelta) {
            // The Delta is rendered whole by interacta-delta-view; a fresh answer
            // gets a one-shot staggered per-block fade instead of the plain-string
            // typewriter (fallback only).
            const fresh =
                this.enableRevealAnimation() && !this.message().alreadyAnimated;
            if (fresh) this.messageAnimated.emit();

            if (
                fresh &&
                !matchMedia('(prefers-reduced-motion: reduce)').matches
            ) {
                this.startDeltaReveal();
            } else {
                this.scrollToBottom.emit();
                this.revealed.emit();
            }
            return;
        }
        if (!this.message().alreadyAnimated) {
            const stringFormatted = formatMessage(this.message().content);
            this.animationSubscription = from(stringFormatted.split(/(\s+)/g))
                .pipe(
                    concatMap((word) =>
                        concat(of(word), timer(20).pipe(ignoreElements())),
                    ),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe({
                    next: (word) =>
                        this.displayedMessage.update((value) => value + word),
                    complete: () => {
                        this.scrollToBottom.emit();
                        this.revealed.emit();
                    },
                });
            this.messageAnimated.emit();
        } else {
            this.displayedMessage.set(formatMessage(this.message().content));
            this.scrollToBottom.emit();
            this.revealed.emit();
        }
    }

    private get deltaViewHost(): HTMLElement | null {
        return this.elementRef.nativeElement.querySelector(
            'interacta-delta-view',
        );
    }

    /**
     * Reveals the answer unit by unit (Gemini style). Hides the whole answer +
     * footer up front (before paint) so nothing flashes, then arms the staggered
     * fade. If Quill hasn't laid out its content yet, retries once on the next
     * frame; if it still isn't there, reveals it plainly so it can never get
     * stuck invisible.
     */
    private startDeltaReveal(): void {
        this.contentRevealed.set(false);
        this.deltaViewHost?.classList.add('ai-delta-hidden');
        if (!this.armReveal()) {
            requestAnimationFrame(() => {
                if (!this.armReveal()) this.revealWithoutAnimation();
            });
        }
    }

    /** Stagger-indexes each unit and starts the fade. Returns false if the answer isn't rendered yet. */
    private armReveal(): boolean {
        const host = this.deltaViewHost;
        const editor = host?.querySelector('.ql-editor');
        const units = editor ? this.collectRevealUnits(editor) : [];
        if (!host || !units.length) return false;

        // Stagger step shrinks as the answer grows, so every unit still fades in
        // sequence (never clumping) while the whole reveal stays bounded in time.
        const step = Math.min(
            REVEAL_UNIT_MS,
            REVEAL_MAX_TOTAL_MS / units.length,
        );
        units.forEach((unit, i) => {
            unit.style.setProperty(
                '--ai-delta-delay',
                `${Math.round(i * step)}`,
            );
            unit.classList.add('ai-reveal-unit');
        });
        this.animateDelta.set(true);
        // Enabling the animation (fill: both → units start at opacity 0) and
        // dropping the blanket hide in the same frame avoids any flash.
        host.classList.add('ai-delta-reveal');
        host.classList.remove('ai-delta-hidden');

        // Reveal the footer once the last unit's fade is done. A single timer is
        // stuck-proof — unlike counting animationend events, which would strand
        // the footer if any unit never animates.
        const total = (units.length - 1) * step + REVEAL_UNIT_MS;
        timer(total)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.contentRevealed.set(true);
                this.revealed.emit();
            });
        return true;
    }

    /** Fallback when Quill never lays out the answer: show it (and the footer) without animation. */
    private revealWithoutAnimation(): void {
        this.deltaViewHost?.classList.remove('ai-delta-hidden');
        this.contentRevealed.set(true);
        this.revealed.emit();
    }

    /**
     * Collects the fade units, in document order, using only Quill's own elements
     * — injecting foreign nodes (e.g. per-sentence spans) is pointless because
     * Quill's MutationObserver strips them to match its Delta model. Each list
     * item reveals on its own; every other non-empty block reveals whole (its
     * inline text, bold, mentions fade together). Empty trailing blocks (Quill
     * always ends with <p><br></p>) are skipped.
     */
    private collectRevealUnits(editor: Element): HTMLElement[] {
        const units: HTMLElement[] = [];
        for (const block of Array.from(editor.children)) {
            if (block.matches('ol, ul')) {
                block
                    .querySelectorAll('li')
                    .forEach((li) => units.push(li as HTMLElement));
            } else if (
                block.textContent?.trim() ||
                block.querySelector('img')
            ) {
                units.push(block as HTMLElement);
            }
        }
        return units;
    }

    /** Skips to the full text, used by the parent to cut short a still-animating message when a new one is sent. */
    completeAnimation(): void {
        if (this.message().messageDelta) {
            return;
        }
        this.animationSubscription?.unsubscribe();
        this.displayedMessage.set(formatMessage(this.message().content));
        this.revealed.emit();
    }

    copyText(): void {
        navigator.clipboard.writeText(this.message().content);
        this.tipService.success('AI.COPY_SUCCESS');
    }

    toggleReferences(): void {
        const expanded = !this.showReferences();
        if (expanded) {
            this.gaService.sendCustomEvent('isp_ai_sources_list_expanded');
        }
        this.showReferences.set(expanded);
    }

    /**
     * The chips live in Quill-rendered DOM, so the click is caught here by
     * delegation and opens the sources popover anchored to the chip.
     */
    onReferenceClick(event: Event): void {
        const chip = (event.target as HTMLElement | null)?.closest<HTMLElement>(
            `.${AI_REFERENCE_CLASS}`,
        );
        if (!chip) return;

        const id = +(chip.dataset[AI_REFERENCE_ID_DATASET_KEY] ?? NaN);
        const sources =
            this.message().deltaReferences?.find(
                (reference) => reference.id === id,
            )?.sources ?? [];

        if (sources.length) {
            // CdkOverlayOrigin injects its own ElementRef, so it can't be built
            // for a foreign element; the overlay connects to a bare ElementRef
            // just as well, only the input type asks for the directive.
            this.openedReference.set({
                id,
                origin: new ElementRef(chip) as unknown as CdkOverlayOrigin,
            });
        }
    }

    onReferenceKeydown(event: KeyboardEvent): void {
        if (
            event.code !== KeyCode.ENTER &&
            event.code !== KeyCode.NUMPAD_ENTER &&
            event.code !== KeyCode.SPACE
        ) {
            return;
        }
        const target = event.target as HTMLElement | null;
        if (!target?.classList.contains(AI_REFERENCE_CLASS)) return;

        event.preventDefault();
        target.click();
    }

    closeReferencePopover(): void {
        this.openedReference.set(null);
    }

    onPopoverAskReference(reference: AISharedGenericReference): void {
        this.closeReferencePopover();
        this.askReference.emit(reference);
    }

    onPopoverSmartAction(event: {
        reference: AISharedGenericReference;
        action: AISharedSmartAction;
    }): void {
        this.closeReferencePopover();
        this.smartAction.emit(event);
    }
}
